import {Form, Formik, FormikProps,} from 'formik';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import * as Yup from 'yup';

import {userSettingsApi} from 'api';
import {Button, DataWrapper, InputFormik, PredefinedNotifierType, UnsavedChangesLeavePrompt} from 'components';
import {useApiData} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {dataUtils} from 'utils';

const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON;
const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.SESSION.CLIENT_SETTINGS.AVATAR;

const FIELDS = {
    HOST: 'host',
    URL: 'url'
};

type Values = {
    host: string;
    url: string;
}

type FormTemplateProps = {
    loading: boolean;
} & FormikProps<Values>;

const FormTemplate: React.FC<FormTemplateProps> = ({loading, initialValues, submitForm, isValid, values}) => {
    const {t} = useTranslation();

    const hasNoChanges = useMemo(() => dataUtils.isEqualValues(values, initialValues), [initialValues, values]);

    return (
        <Form className='tw-relative tw-mt-8'>
            <div className='tw-mb-2'>
                {t(T_KEYS.DESCRIPTION)}
            </div>
            <DataWrapper isLoading={loading} hideContentOnLoading={false}>
                <InputFormik
                    name={FIELDS.HOST}
                    label={t(T_KEYS.HOST)}
                    className='tw-w-full'
                    spacing='top'
                />

                <InputFormik
                    name={FIELDS.URL}
                    label={t(T_KEYS.URL)}
                    className='tw-w-full'
                    spacing='top'
                />

                <div className='tw-text-right tw-mt-controls'>
                    <Button
                        color='primary'
                        disabled={hasNoChanges || !isValid}
                        loading={loading}
                        onClick={submitForm}
                    >
                        {t(COMMON_T_KEYS.BUTTONS.SAVE)}
                    </Button>
                </div>

                <UnsavedChangesLeavePrompt values={values} initialValues={initialValues} />
            </DataWrapper>
        </Form>
    );
};

export const AvatarSettings: React.FC = () => {
    const [initialValues, setInitialValues] = useState<Values>({
        host: '',
        url: ''
    });

    const validationSchema = useMemo(() => Yup.object().shape({
        [FIELDS.HOST]: Yup.string(),
        [FIELDS.URL]: Yup.string()
    }), []);

    const {send: fetchConfig, loading: fetchLoading} =
        useApiData(userSettingsApi.getAvatarConfig, {notificationType: PredefinedNotifierType.FetchError, onSuccess: setInitialValues});

    const fetch = useCallback(() => {
        fetchConfig(null);
    }, [fetchConfig]);

    const {send: updateConfig, loading: updateLoading} =
        useApiData(userSettingsApi.updateAvatarConfig, {notificationType: PredefinedNotifierType.Save, onSuccess: fetch});

    const onSubmit = useCallback((values: Values) => {
        updateConfig({groupId: null, config: values});
    }, [updateConfig]);

    useEffect(() => {
        fetch();
    }, [fetch]);

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={onSubmit}
        >
            {(props) => (
                <FormTemplate
                    {...props}
                    loading={fetchLoading || updateLoading}
                />
            )}
        </Formik>
    );

};